import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  createLocalEstoque,
  getEstabelecimentos,
  getLocaisEstoque,
  updateLocalEstoque,
} from '../services/api';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import useFeedbackAlert from './common/useFeedbackAlert';

const vazio = { id: 0, estabelecimentoId: '', nome: '', ativo: true, principal: false };

export default function LocaisEstoquePage() {
  const { showSuccess, showApiError, FeedbackUi } = useFeedbackAlert();
  const [filiais, setFiliais] = useState([]);
  const [filialId, setFilialId] = useState('');
  const [locais, setLocais] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(vazio);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async (estab) => {
    const { data } = await getLocaisEstoque(estab || undefined);
    setLocais(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => {
    getEstabelecimentos()
      .then(({ data }) => {
        const lista = Array.isArray(data) ? data : (data ? [data] : []);
        setFiliais(lista);
        if (lista.length === 1) setFilialId(String(lista[0].id));
      })
      .catch(showApiError);
  }, [showApiError]);

  useEffect(() => {
    carregar(filialId).catch(showApiError);
  }, [carregar, filialId, showApiError]);

  const nomeFilial = (id) => {
    const f = filiais.find((x) => Number(x.id) === Number(id));
    return f?.nomeFantasia || f?.razaoSocial || `Filial ${id}`;
  };

  const abrirNovo = () => {
    setForm({
      ...vazio,
      estabelecimentoId: filialId || (filiais[0] ? String(filiais[0].id) : ''),
    });
    setDlg(true);
  };

  const abrirEdicao = (local) => {
    setForm({
      id: local.id,
      estabelecimentoId: String(local.estabelecimentoId),
      nome: local.nome,
      ativo: local.ativo !== false,
      principal: !!local.principal,
    });
    setDlg(true);
  };

  const salvar = async () => {
    const nome = form.nome.trim();
    if (!nome || !form.estabelecimentoId) {
      showApiError({ response: { data: { error: 'Informe a filial e o nome do local.' } } });
      return;
    }
    setSalvando(true);
    try {
      const payload = {
        estabelecimentoId: Number(form.estabelecimentoId),
        nome,
        ativo: form.principal ? true : form.ativo,
      };
      if (form.id) await updateLocalEstoque(form.id, payload);
      else await createLocalEstoque(payload);
      setDlg(false);
      showSuccess(form.id ? 'Local atualizado.' : 'Local criado.');
      await carregar(filialId);
    } catch (e) {
      showApiError(e);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Locais de estoque"
        subtitle="Cada filial começa com o Estoque principal. Loja, sobreloja e depósito guardam partes do mesmo produto."
        actions={(
          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNovo} sx={{ textTransform: 'none' }}>
            Novo local
          </Button>
        )}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
        <TextField
          select
          label="Filial"
          size="small"
          value={filialId}
          onChange={(e) => setFilialId(e.target.value)}
          sx={{ minWidth: 240 }}
        >
          <MenuItem value="">Todas</MenuItem>
          {filiais.map((f) => (
            <MenuItem key={f.id} value={String(f.id)}>
              {f.nomeFantasia || f.razaoSocial}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Local</TableCell>
              <TableCell>Filial</TableCell>
              <TableCell>Situação</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {locais.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">Nenhum local nesta filial.</TableCell>
              </TableRow>
            )}
            {locais.map((l) => (
              <TableRow key={l.id}>
                <TableCell>{l.nome}</TableCell>
                <TableCell>{nomeFilial(l.estabelecimentoId)}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    {l.principal && <Chip size="small" color="primary" label="Principal" />}
                    <Chip size="small" color={l.ativo ? 'success' : 'default'} label={l.ativo ? 'Ativo' : 'Inativo'} />
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => abrirEdicao(l)}>Editar</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>

      <Dialog open={dlg} onClose={() => setDlg(false)} fullWidth maxWidth="xs">
        <DialogTitle>{form.id ? 'Editar local' : 'Novo local'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Filial"
              value={form.estabelecimentoId}
              onChange={(e) => setForm((p) => ({ ...p, estabelecimentoId: e.target.value }))}
              disabled={!!form.id}
              fullWidth
            >
              {filiais.map((f) => (
                <MenuItem key={f.id} value={String(f.id)}>
                  {f.nomeFantasia || f.razaoSocial}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Nome"
              value={form.nome}
              onChange={(e) => setForm((p) => ({ ...p, nome: e.target.value }))}
              fullWidth
              autoFocus
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={form.principal ? true : form.ativo}
                  disabled={form.principal}
                  onChange={(e) => setForm((p) => ({ ...p, ativo: e.target.checked }))}
                />
              )}
              label={form.principal ? 'Estoque principal (sempre ativo)' : 'Ativo'}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg(false)}>Cancelar</Button>
          <Button variant="contained" onClick={salvar} disabled={salvando}>Salvar</Button>
        </DialogActions>
      </Dialog>
      <FeedbackUi />
    </Box>
  );
}
